import { useCallback, useMemo } from "react";
import { Copy, ShareNetwork, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useReferralSummary } from "../hooks/useReferralSummary";
import { HashLoader } from "react-spinners";

const cardShell = (dashboard: boolean) =>
  dashboard
    ? "bg-white drop-shadow-sm rounded-xl p-5"
    : "bg-white rounded-2xl border border-gray-200 p-6 shadow-sm";

/**
 * Shows Core referral code + invite link (Profile, Dashboard, Settings).
 */
export default function ReferralInviteCard({
  dashboard = false,
}: {
  /** Match main app dashboard cards (drop-shadow, rounded-xl). */
  dashboard?: boolean;
}) {
  const { data, isLoading, isError, error, refetch } = useReferralSummary();

  const summary = data?.data;
  const code = summary?.referralCode;

  const inviteUrl = useMemo(() => {
    if (!code) return "";
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/sign-up?ref=${encodeURIComponent(code)}`;
  }, [code]);

  const copyCode = useCallback(() => {
    if (!code) return;
    void navigator.clipboard.writeText(code).then(() => {
      toast.success("Referral code copied");
    });
  }, [code]);

  const copyLink = useCallback(() => {
    if (!inviteUrl) return;
    void navigator.clipboard.writeText(inviteUrl).then(() => {
      toast.success("Invite link copied");
    });
  }, [inviteUrl]);

  const share = useCallback(async () => {
    if (!inviteUrl || !code) return;
    const shareData = {
      title: "Join me on Visaro",
      text: `Use my code ${code} when you sign up.`,
      url: inviteUrl,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(inviteUrl);
        toast.success("Invite link copied");
      }
    } catch {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Invite link copied");
    }
  }, [inviteUrl, code]);

  if (isLoading) {
    return (
      <div
        className={`${cardShell(dashboard)} flex items-center justify-center gap-3`}
      >
        <HashLoader size={24} color="#EA5C29" />
        <span className="text-gray-600 text-sm">Loading referral…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className={`${
          dashboard
            ? "bg-amber-50 drop-shadow-sm rounded-xl p-4"
            : "bg-amber-50 rounded-2xl border border-amber-100 p-4"
        } text-sm text-amber-900`}
      >
        <p className="font-medium">Could not load referral info</p>
        <p className="text-amber-800/90 mt-1">
          {(error as Error)?.message ?? "Try again later."}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-2 text-visaro-orange font-semibold hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!code) {
    return (
      <div className={cardShell(dashboard)}>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-orange-50 text-visaro-orange">
            <Gift size={24} weight="duotone" />
          </div>
          <div>
            <h3 className="font-semibold text-deepBlue">Invite friends</h3>
            <p className="text-gray-600 text-sm mt-1">
              Your referral code will appear here after your account is fully
              set up.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardShell(dashboard)}>
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-xl bg-orange-50 text-visaro-orange">
          <Gift size={24} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-deepBlue text-lg">Invite friends</h3>
          <p className="text-gray-600 text-sm mt-0.5">
            Share your code or link. Rewards apply when Visaro referral rules
            are met.
          </p>
          {summary?.hasReferralAttribution && summary.referredBy && (
            <p className="text-xs text-gray-500 mt-2">
              You joined with a referral ({summary.referredBy.kind}:{" "}
              <span className="font-mono">{summary.referredBy.code}</span>).
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 font-mono text-lg font-semibold tracking-wide text-deepBlue bg-gray-50 px-4 py-3 rounded-xl border border-gray-100">
          {code}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={copyCode}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-deepBlue text-white text-sm font-semibold hover:bg-deepBlue/90 transition-colors"
          >
            <Copy size={18} weight="bold" />
            Copy code
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-deepBlue text-sm font-semibold hover:border-visaro-orange transition-colors"
          >
            <Copy size={18} />
            Copy link
          </button>
          <button
            type="button"
            onClick={() => void share()}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-visaro-orange text-visaro-orange text-sm font-semibold hover:bg-orange-50 transition-colors"
          >
            <ShareNetwork size={18} weight="bold" />
            Share
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-3 break-all">{inviteUrl}</p>
    </div>
  );
}
